/**
 * The head of the modular figures — the slender head, the only head shape — drawn by hand
 * pixel by pixel, the way paper-doll systems such as LPC build heads, rather than computed from
 * curves, so every edge steps cleanly. One grid per camera view; profile and three-quarter grids
 * face screen-right and are mirrored at draw time for the other headings. The fine face features
 * (face2.ts) are laid on it at full resolution.
 *
 * Variety comes from the layers over it: hair, hats and faces. Grid legend: `.` empty, `s` skin,
 * `e` ear (skin shade), `c` cheekbone light, `S` soft hollow under the cheekbone.
 */
import type { View } from '../sprites';

export interface HeadTemplate {
  rows: Record<View, readonly string[]>;
  /** Grid column and row that sit on the head joint. */
  ox: number;
  oy: number;
}

/**
 * The head, from the slender head (หน้าเรียว) traced from a reference drawn over the old
 * egg head: its round skull down to the eyes (the skull the hairstyles and hats were fitted to), the
 * cheeks full to below the eyes, then turning in round to a small chin four pixels wide, two rows
 * below the mouth (the mouth a third of the way from the nose to the chin); the ears set lower,
 * level with the eyes and nose, as on a real head. Turned views curve the jaw at the back and bring
 * the chin forward; in profile the skull rounds well behind the ear, the nose stands out level with
 * the reference's and the chin is set back a little.
 */
export const HEAD: HeadTemplate = {
  ox: 9,
  oy: 9,
  rows: {
    front: [
      '......ssssss......',
      '....ssssssssss....',
      '...ssssssssssss...',
      '..ssssssssssssss..',
      '..ssssssssssssss..',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      'esssssssssssssssse',
      'esssssssssssssssse',
      'essccssssssssccsse',
      'esssssssssssssssse',
      '..ssssssssssssss..',
      '..ssssssssssssss..',
      '...ssssssssssss...',
      '....ssssssssss....',
      '.....ssssssss.....',
      '.......ssss.......',
    ],
    front3: [
      '......ssssss......',
      '....ssssssssss....',
      '...ssssssssssss...',
      '..ssssssssssssss..',
      '..ssssssssssssss..',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.esssssssssssssss.',
      '.eesssssssssccsss.',
      '..essssssssssssss.',
      '...ssssssssssssss.',
      '...sssssssssssss..',
      '....ssssssssssss..',
      '.....ssssssssss...',
      '......ssssssss....',
      '........sssss.....',
    ],
    side: [
      '......sssssss.....',
      '....sssssssssss...',
      '...sssssssssssss..',
      '..ssssssssssssss..',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '..ssssessssssssss.',
      '...ssseesssssssss.',
      '....sssssssssssss.',
      '....ssssssssssssss',
      '.....ssssssssssss.',
      '......sssssssssss.',
      '......sssssssssss.',
      '.......sssssssss..',
      '.........ssssss...',
    ],
    back3: [
      '......ssssss......',
      '....ssssssssss....',
      '...ssssssssssss...',
      '..ssssssssssssss..',
      '..ssssssssssssss..',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      'essssssssssssssse.',
      'essssssssssssssse.',
      '.sssssssssssssss..',
      '...ssssssssssss...',
      '...ssssssssssss...',
      '....ssssssssss....',
      '.....ssssssss.....',
      '......ssssss......',
      '.......ssss.......',
    ],
    back: [
      '......ssssss......',
      '....ssssssssss....',
      '...ssssssssssss...',
      '..ssssssssssssss..',
      '..ssssssssssssss..',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      '.ssssssssssssssss.',
      'esssssssssssssssse',
      'esssssssssssssssse',
      'esssssssssssssssse',
      'esssssssssssssssse',
      '..ssssssssssssss..',
      '..ssssssssssssss..',
      '...ssssssssssss...',
      '....ssssssssss....',
      '.....ssssssss.....',
      '.......ssss.......',
    ],
  },
};
